import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getUrlQuery } from "@util/helpers";

type SmallCardProps = {
  /** The article data to be displayed in the card */
  article: SanitizedSectionArticle;
  /** Optional className for the card */
  className?: string;
  /** Optional content class name */
  contentClassName?: string;
  /** Whether to display the edited timestamp of the article */
  displayUpdated?: boolean;
  /** Optional link parameters for the article */
  linkParams?: URLSearchParams;
  /** Indicator to display the image */
  displayImage?: boolean;
  /** Indicator to disable paywall */
  disablePaywall?: boolean;
};

/**
 * SmallCard component to display a small article card.
 * It renders a BasicCard with specific variations for small cards.
 */
const SmallCard = ({
  article,
  className,
  contentClassName,
  displayUpdated,
  linkParams,
  displayImage = false,
  disablePaywall,
}: SmallCardProps): React.ReactElement => {
  const CardVariations: CardVariations = {
    kicker: {
      color: "text-gray-515",
    },
    content: {
      width: displayImage ? cn("w-[calc(100%-5rem)]") : "w-full",
      extraClass: cn("px-3 break-words", contentClassName),
    },
    image: {
      position: "right",
      width: displayImage ? cn("w-[5rem]") : "w-full",
      extraClass: cn("pl-3"),
      aspectRatio: "aspect-square object-cover",
    },
  };

  const params = getUrlQuery(article.urlPath, linkParams);

  return (
    <BasicCard
      id={article.id}
      title={article.title}
      kicker={article.kicker?.fields?.[0]?.value}
      slug={`${article.urlPath}${params}`}
      section={article.sections?.[0]}
      variations={CardVariations}
      rootClassName={className}
      updated={displayUpdated ? article.updated : undefined}
      media={displayImage ? article.media : undefined}
      paywall={!disablePaywall ? article.paywall?.contentAccess === "1" : false}
    />
  );
};

export default SmallCard;
